<div class="space-y-4">
  <app-breadcrumbs [items]="breadcrumbItems" />

  <section class="grid items-start gap-4 xl:grid-cols-[minmax(0,1.8fr)_minmax(340px,1fr)]">
    <article class="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
      @if (loading()) {
        <div class="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
          Loading partner organizations...
        </div>
      } @else if (errorMessage()) {
        <div class="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {{ errorMessage() }}
        </div>
      } @else {
        <div class="space-y-3">
          <div class="grid gap-3 border-b border-slate-100 pb-3">
            <div>
              <h2 class="text-sm font-semibold uppercase tracking-wide text-slate-500">
                Partner Organizations ({{ filteredPartners().length }})
              </h2>
              <p class="mt-1 text-xs text-slate-500">Search by partner name and/or supported service(s).</p>
            </div>

            <div>
              <label for="partner-list-search" class="mb-1 block text-xs font-medium text-slate-600">Search by name</label>
              <input
                id="partner-list-search"
                type="text"
                class="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
                placeholder="Type a partner organization name"
                [ngModel]="partnerSearchQuery()"
                (ngModelChange)="setPartnerSearchQuery($event)"
                [ngModelOptions]="{ standalone: true }"
              />
            </div>

            <div class="space-y-1">
              <p class="text-xs font-medium text-slate-600">Supported Services</p>

              <div class="flex flex-wrap gap-2">
                @for (service of services(); track service.id) {
                  <button
                    type="button"
                    class="filter-chip rounded-lg border px-3 py-1.5 text-sm"
                    [class.filter-chip-selected]="isPartnerServiceFilterSelected(service.id)"
                    (click)="togglePartnerServiceFilter(service.id)"
                  >
                    {{ service.name }}
                  </button>
                }
              </div>
            </div>
          </div>

          @if (!filteredPartners().length) {
            <div class="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
              No partner organizations match the current filters.
            </div>
          }

          @for (partner of filteredPartners(); track trackById($index, partner)) {
            <article class="rounded-xl border border-slate-200 p-4">
              <div class="grid gap-3 md:grid-cols-[minmax(0,1fr)_auto] md:items-start">
                <div>
                  <h3 class="text-base font-semibold text-slate-900">{{ partner.name }}</h3>

                  <div class="mt-2 flex flex-wrap gap-2">
                    @if (partner.approvedServiceIds.length) {
                      @for (serviceLabel of approvedServiceLabels(partner); track serviceLabel) {
                        <span class="rounded-full border border-slate-300 bg-slate-50 px-2 py-0.5 text-xs text-slate-700">
                          {{ serviceLabel }}
                        </span>
                      }
                    } @else {
                      <span class="text-sm text-slate-500">No approved services assigned.</span>
                    }
                  </div>
                </div>

                <div class="flex flex-wrap gap-2 md:justify-end">
                  <button
                    type="button"
                    class="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
                    (click)="startEdit(partner)"
                  >
                    Edit
                  </button>
                </div>
              </div>
            </article>
          }
        </div>
      }
    </article>

    <aside class="self-start rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
      <div class="mb-4 border-b border-slate-100 pb-4">
        <h2 class="text-lg font-semibold text-slate-900">
          {{ isEditing() ? 'Edit Partner Organization' : 'Create Partner Organization' }}
        </h2>
        <p class="mt-1 text-sm text-slate-500">
          Set the organization name and approved services.
        </p>
      </div>

      <form class="space-y-4" [formGroup]="partnerForm" (ngSubmit)="submit()">
        <div>
          <label for="partner-name" class="mb-1 block text-sm font-medium text-slate-700">Organization Name</label>
          <div class="relative">
            <input
              id="partner-name"
              type="text"
              formControlName="name"
              class="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
              [class.bg-slate-100]="isPartnerNameLocked()"
              [class.pr-12]="isPartnerNameLocked()"
              [readonly]="isPartnerNameLocked()"
              [attr.aria-disabled]="isPartnerNameLocked()"
              placeholder="Enter organization name"
            />

            @if (isPartnerNameLocked()) {
              <button
                type="button"
                class="absolute inset-0 flex cursor-pointer items-center justify-end rounded-xl pr-3 text-slate-500 hover:bg-slate-200/60 hover:text-slate-800"
                aria-label="Edit partner organization name"
                title="Edit organization name"
                (click)="requestPartnerNameUnlock()"
              >
                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" class="h-4 w-4">
                  <path
                    d="M12 20h9"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                  />
                  <path
                    d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5Z"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>
            }
          </div>
        </div>

        <div class="space-y-2">
          <label class="block text-sm font-medium text-slate-700">Approved Services</label>

          @if (!services().length) {
            <div class="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">
              No services are available yet.
            </div>
          } @else {
            <div class="max-h-72 space-y-2 overflow-y-auto rounded-xl border border-slate-200 p-3">
              @for (service of services(); track trackById($index, service)) {
                <label class="flex cursor-pointer items-start gap-3 rounded-lg border border-slate-200 p-2 hover:bg-slate-50">
                  <input
                    type="checkbox"
                    class="mt-0.5 h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-400"
                    [checked]="isServiceSelected(service.id)"
                    (change)="toggleServiceSelection(service.id)"
                    [disabled]="saving()"
                  />
                  <span class="min-w-0">
                    <span class="block text-sm font-medium text-slate-900">{{ service.name }}</span>
                    <span class="block text-xs text-slate-500">
                      Unit: {{ service.service_unit_label }}
                      @if (!service.isActive) {
                        <span class="font-semibold text-amber-700">(Inactive)</span>
                      }
                    </span>
                  </span>
                </label>
              }
            </div>
          }
        </div>

        <div class="flex gap-2">
          <button
            type="submit"
            class="rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-70"
            [disabled]="saving()"
          >
            {{ isEditing() ? 'Save Changes' : 'Create Partner' }}
          </button>

          <button
            type="button"
            class="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            (click)="startCreate()"
          >
            Reset
          </button>
        </div>
      </form>
    </aside>
  </section>

  @if (partnerNameWarningOpen()) {
    <div class="fixed inset-0 z-40 bg-slate-900/50" (click)="cancelPartnerNameUnlock()"></div>

    <div class="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="partner-name-warning-title">
      <article class="w-full max-w-lg rounded-2xl border border-slate-200 bg-white p-5 shadow-xl sm:p-6">
        <h3 id="partner-name-warning-title" class="text-lg font-semibold text-slate-900">Change Partner Name?</h3>

        <div class="mt-3 space-y-3 text-sm leading-relaxed text-slate-600">
          <p>
            Changing this partner organization name will affect all past and future cases assigned to this partner.
          </p>
          <p>
            Any reports involving this partner will also use the updated name for both past and future cases assigned to this partner.
          </p>
          <p>
            If this is a major change to the partner organization, creating a new partner may be more appropriate.
          </p>
        </div>

        <div class="mt-5 flex justify-end gap-2">
          <button
            type="button"
            class="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            (click)="cancelPartnerNameUnlock()"
          >
            Cancel
          </button>

          <button
            type="button"
            class="rounded-xl bg-amber-700 px-4 py-2 text-sm font-medium text-white hover:bg-amber-600"
            (click)="confirmPartnerNameUnlock()"
          >
            Continue Editing Name
          </button>
        </div>
      </article>
    </div>
  }
</div>
